/**
 * Right panel — Backend-UI `net-exposure-tracker` + CM Betfair book rows
 * (`work-station.html`: Total(X), Betfair Book - betfair, Betfair Book - local).
 *
 * Family icon = Lab `showDownLine` / `View Downline Book`: expand loads direct
 * children via `getParticularBooks` → CM `getTotalBooks1` per child.
 *
 * Total(X) multiplier: Lab `get|setNetExpoPageCommissionRate` via CM-API.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { useToast } from '@w7/ui'
import {
  useDownlineParticularBooks,
  useFancyPtLadder,
  useNetExpoPageCommissionRate,
  useSetNetExpoPageCommissionRate,
} from '@/api/hooks/net-exposure/useWorkstation'
import {
  useViewerCurrency,
  useViewerCurrencyRaw,
} from '@/api/hooks/shared/useViewerCurrency'
import { lineSessionMoneyOpts } from '@/features/net-exposure/lineSessionDisplayMoney'
import { money } from '@/lib/format'
import {
  emptyBetfairCompareMatch,
  isLineSessionMarket,
  type BetfairCompareView,
  type FancyPtLadderRow,
} from '@/api/map/workstation'
import type { BookType } from '../fixture'
import { labeledMarketName } from '../marketSourceLabel'
import {
  treeHorseAmount,
  treeNetExposure,
  type WorkstationBook,
  type WorkstationMarketType,
  type WorkstationRunner,
  type WorkstationTreeNode,
} from '../workstation-fixture'
import {
  DEFAULT_NET_EXPO_PAGE_COMMISSION_RATE,
  isNetExpoPageCommissionRateSaved,
  parseNetExpoPageCommissionRate,
} from './access'

const ICON = {
  levelUp: '/net-exposure/level-up.svg',
  familyTree: '/net-exposure/family-tree-primary.svg',
  familyTreeFancy: '/net-exposure/family-tree.svg',
  ladder: '/net-exposure/ladder.svg',
  closeDownline: '/net-exposure/sort-amount-asc.svg',
} as const

function toneClass(value: number): string {
  return value < 0 ? 'w7-ws-amt-neg' : 'w7-ws-amt-pos'
}

function Amt({
  value,
  bookType,
  level,
  lineSession = false,
}: {
  value: number
  bookType: BookType
  level: string
  /** Betfair line session only — Lab dashboard convert; skip POINT@1. */
  lineSession?: boolean
}) {
  const viewerCurrency = useViewerCurrency()
  const rawCurrency = useViewerCurrencyRaw()
  void bookType
  const lsOpts = lineSession ? lineSessionMoneyOpts(rawCurrency) : null
  const currency = lsOpts ? lsOpts.currency : viewerCurrency
  const body = money(value, {
    round: true,
    symbol: false,
    currency,
    ...(lsOpts ? { alreadyDisplay: lsOpts.alreadyDisplay } : {}),
  })
  // INR: Rs. on My PT and Total Book for non-player rows (player stays bare).
  const prefix = currency === 'INR' && level !== 'player' ? 'Rs.' : ''
  const suffix = currency !== 'INR' ? ' Pts' : ''
  return (
    <span className={toneClass(value)}>
      {prefix}
      {body}
      {suffix}
    </span>
  )
}

function padRunnerCount(count: number, type?: WorkstationMarketType): number {
  // Fancy with no No|Yes horses yet: do not invent a blank Match Odds column.
  if (type === 'fancy' && count === 0) return 0
  // Two-runner Match Odds markets should not invent a blank Draw/X column.
  return Math.max(1, count)
}

function amountForRunner(
  compare: BetfairCompareView,
  runnerId: string,
  side: 'local' | 'betfair',
  runnerIndex: number,
): number {
  const row = compare.rows.find((r) => r.runnerId === runnerId)
  if (row) return side === 'local' ? row.localAmount : row.betfairAmount
  // Fancy/line often has one selection id that does not match Yes/No runner ids —
  // Lab still paints the line amount in the first runner column.
  if (compare.kind === 'fancy' && runnerIndex === 0 && compare.rows[0]) {
    return side === 'local' ? compare.rows[0].localAmount : compare.rows[0].betfairAmount
  }
  return 0
}

export function ladderRunsLabel(row: FancyPtLadderRow): string {
  if (row.endScore === 'Infinite') return `${row.startScore} +`
  return `${row.startScore} - ${row.endScore}`
}

/** Fancy PT ladder icons — multiple hierarchy rows (e.g. CM + TM) may stay open. */
export function nextPtLadderOpen(prev: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(prev)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  return next
}

/**
 * Lab `PT-ladder` strip under a fancy/session tree row.
 * Same columns as agent: Runs | Amount (no Commission Amount).
 */
export function PtLadderPanel({
  rows,
  loading,
  cols,
  lineSession = false,
}: {
  rows: FancyPtLadderRow[]
  loading: boolean
  cols: number
  lineSession?: boolean
}) {
  const viewerCurrency = useViewerCurrency()
  const rawCurrency = useViewerCurrencyRaw()
  const lsOpts = lineSession ? lineSessionMoneyOpts(rawCurrency) : null
  const currency = lsOpts ? lsOpts.currency : viewerCurrency
  const moneyOpts = {
    round: true as const,
    symbol: false as const,
    currency,
    ...(lsOpts ? { alreadyDisplay: lsOpts.alreadyDisplay } : {}),
  }
  // Match agent PT ladder: Runs | Amount only (no Commission Amount).
  const colSpan = 3 + cols + (lineSession ? 1 : 0)
  return (
    <tr className="w7-ws-pt-ladder-row">
      <td className="w7-ws-pt-ladder" colSpan={colSpan}>
        {loading && rows.length === 0 ? (
          <div className="w7-ws-pt-ladder-empty">Loading ladder…</div>
        ) : rows.length === 0 ? (
          <div className="w7-ws-pt-ladder-empty">No ladder</div>
        ) : (
          <table className="w7-ws-pt-ladder-table">
            <thead>
              <tr>
                <th>Runs</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.startScore}-${row.endScore}`}>
                  <td>{ladderRunsLabel(row)}</td>
                  <td className={toneClass(row.amount)}>
                    {money(row.amount, moneyOpts)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </td>
    </tr>
  )
}

function TreeRow({
  node,
  runners,
  type,
  bookType,
  depth,
  open,
  onToggle,
  marketId,
  live,
  ladderOpen,
  onToggleLadder,
  lineSession,
}: {
  node: WorkstationTreeNode
  runners: WorkstationRunner[]
  type: WorkstationMarketType
  bookType: BookType
  depth: number
  open: Set<string>
  onToggle: (id: string) => void
  marketId: string
  live: boolean
  ladderOpen: ReadonlySet<string>
  onToggleLadder: (id: string) => void
  /** Betfair line session — ladder type betfair-line-sessions. */
  lineSession: boolean
}) {
  // Lab: family icon for every non-player (even before children are loaded).
  const canExpand = node.level !== 'player'
  const isOpen = open.has(node.id)
  const ne = treeNetExposure(node, type, bookType)
  const cols = padRunnerCount(runners.length, type)
  const treeIcon = type === 'fancy' ? ICON.familyTreeFancy : ICON.familyTree
  const showLadder = type === 'fancy' && ladderOpen.has(node.id)
  const ladderQ = useFancyPtLadder({
    marketId,
    userId: node.id,
    accessLevel: node.accessLevel,
    selectedType: bookType,
    nodeLevel: node.level,
    enabled: live && showLadder,
    lineSession,
  })

  return (
    <>
      <tr className="w7-ws-tree-row" data-depth={depth} data-level={node.level}>
        <td className="w7-ws-tree-user" style={{ paddingLeft: `${8 + depth * 16}px` }}>
          <img
            className={`w7-ws-icon w7-ws-icon-level${node.level === 'player' ? ' is-player' : ''}`}
            src={ICON.levelUp}
            alt=""
            width={14}
            height={14}
          />
          <span className="w7-ws-tree-name">{node.username}</span>
        </td>
        <td className="w7-ws-tree-expand">
          <span className="w7-ws-tree-actions">
            {type === 'fancy' ? (
              <button
                type="button"
                className={`w7-ws-icon-btn${showLadder ? ' is-active' : ''}`}
                title="PT ladder"
                aria-pressed={showLadder}
                onClick={() => onToggleLadder(node.id)}
              >
                <img className="w7-ws-icon" src={ICON.ladder} alt="" width={16} height={16} />
              </button>
            ) : null}
            {canExpand ? (
              <button
                type="button"
                className="w7-ws-tree-toggle"
                aria-expanded={isOpen}
                title={isOpen ? 'Close Downline Book' : 'View Downline Book'}
                onClick={() => onToggle(node.id)}
              >
                <img
                  className="w7-ws-icon"
                  src={isOpen ? ICON.closeDownline : treeIcon}
                  alt=""
                  width={16}
                  height={16}
                />
              </button>
            ) : null}
          </span>
        </td>
        <td className="w7-ws-tree-ne">
          <Amt
            value={ne}
            bookType={bookType}
            level={node.level}
            lineSession={lineSession}
          />
        </td>
        {/* Lab CM line session: netexpoCommision column after NE (not fancy MO horses). */}
        {lineSession ? (
          <td className="w7-ws-tree-cell w7-ws-tree-comm">
            <Amt
              value={node.netexpoCommision ?? 0}
              bookType={bookType}
              level={node.level}
              lineSession={lineSession}
            />
          </td>
        ) : null}
        {Array.from({ length: cols }, (_, i) => {
          // Betfair LS: Lab leaves runner pads empty (amounts live on PT-ladder).
          if (lineSession) {
            return (
              <td key={runners[i]?.id ?? `pad-${i}`} className="w7-ws-tree-cell">
                <span className="w7-ne-dash">&nbsp;</span>
              </td>
            )
          }
          const runner = runners[i]
          if (!runner) {
            return (
              <td key={`pad-${i}`} className="w7-ws-tree-cell">
                <span className="w7-ne-dash">-</span>
              </td>
            )
          }
          const amount = treeHorseAmount(node, runner.id, bookType)
          return (
            <td key={runner.id} className="w7-ws-tree-cell">
              <Amt
                value={amount}
                bookType={bookType}
                level={node.level}
                lineSession={lineSession}
              />
            </td>
          )
        })}
      </tr>
      {showLadder ? (
        <PtLadderPanel
          rows={ladderQ.data?.ladder ?? []}
          loading={ladderQ.isLoading || ladderQ.isFetching}
          cols={cols}
          lineSession={lineSession}
        />
      ) : null}
      {canExpand && isOpen ? (
        <DownlineBranch
          parent={node}
          runners={runners}
          type={type}
          bookType={bookType}
          depth={depth}
          open={open}
          onToggle={onToggle}
          marketId={marketId}
          live={live}
          ladderOpen={ladderOpen}
          onToggleLadder={onToggleLadder}
          lineSession={lineSession}
        />
      ) : null}
    </>
  )
}

/** Lab recursive `<net-exposure-tracker>` children under an expanded row. */
function DownlineBranch({
  parent,
  runners,
  type,
  bookType,
  depth,
  open,
  onToggle,
  marketId,
  live,
  ladderOpen,
  onToggleLadder,
  lineSession,
}: {
  parent: WorkstationTreeNode
  runners: WorkstationRunner[]
  type: WorkstationMarketType
  bookType: BookType
  depth: number
  open: Set<string>
  onToggle: (id: string) => void
  marketId: string
  live: boolean
  ladderOpen: ReadonlySet<string>
  onToggleLadder: (id: string) => void
  lineSession: boolean
}) {
  const seeded = parent.children && parent.children.length > 0
  const q = useDownlineParticularBooks({
    parentUserId: parent.id,
    marketId,
    selectedType: bookType,
    runners,
    marketType: type,
    enabled: live && !seeded,
  })
  const kids = seeded ? parent.children! : (q.data ?? [])
  const cols = padRunnerCount(runners.length, type)

  if (!seeded && q.isLoading && !q.data) {
    return (
      <tr className="w7-ws-tree-row" data-depth={depth + 1}>
        <td className="w7-ws-tree-user" style={{ paddingLeft: `${8 + (depth + 1) * 16}px` }}>
          Loading downline…
        </td>
        <td className="w7-ws-tree-expand" />
        <td className="w7-ws-tree-ne" />
        {lineSession ? <td className="w7-ws-tree-cell w7-ws-tree-comm" /> : null}
        {Array.from({ length: cols }, (_, i) => (
          <td key={`load-${i}`} className="w7-ws-tree-cell" />
        ))}
      </tr>
    )
  }

  if (!seeded && q.isError) {
    return (
      <tr className="w7-ws-tree-row" data-depth={depth + 1}>
        <td
          className="w7-ws-tree-user"
          colSpan={3 + cols}
          style={{ paddingLeft: `${8 + (depth + 1) * 16}px` }}
        >
          Could not load downline
          {q.error instanceof Error && q.error.message ? `: ${q.error.message}` : ''}
        </td>
      </tr>
    )
  }

  return (
    <>
      {kids.map((child) => (
        <TreeRow
          key={child.id}
          node={child}
          runners={runners}
          type={type}
          bookType={bookType}
          depth={depth + 1}
          open={open}
          onToggle={onToggle}
          marketId={marketId}
          live={live}
          ladderOpen={ladderOpen}
          onToggleLadder={onToggleLadder}
          lineSession={lineSession}
        />
      ))}
    </>
  )
}

/**
 * Lab Betfair Book panel (Total X + BF betfair/local) is for Match Odds and
 * Betfair line-session markets only — not bookmaker or custom fancy.
 */
export function showBetfairBookPanel(book: {
  type: WorkstationMarketType
  marketId: string
  marketName: string
  lineLadder?: boolean
}): boolean {
  if (book.type === 'match') return true
  if (book.type === 'bookmaker') return false
  return isLineSessionMarket({
    marketId: book.marketId,
    marketName: book.marketName,
    lineLadder: book.lineLadder,
  })
}

/**
 * Lab LS workstation: Betfair Book betfair/local show NE=0 and Commission=0
 * (empty BF snapshot); runner pads stay blank — PT ladder holds the amounts.
 */
export function lineSessionBetfairBookDisplayAmount(
  lineSession: boolean,
  liveAmount: number,
): number {
  return lineSession ? 0 : liveAmount
}

/**
 * Total (X) + update BF rate — Match Odds only.
 * Line Session workstation keeps BF betfair/local rows; rate update is removed.
 */
export function showBetfairTotalXRateRow(lineSession: boolean): boolean {
  return !lineSession
}

/** Lab CM rows above the downline tracker (`work-station.html`). */
function BetfairBookRows({
  book,
  bookType,
  compare,
  runners,
}: {
  book: WorkstationBook
  bookType: BookType
  compare: BetfairCompareView
  runners: WorkstationRunner[]
}) {
  const toast = useToast()
  const rateQ = useNetExpoPageCommissionRate()
  const setRate = useSetNetExpoPageCommissionRate()
  const serverRate =
    rateQ.data && Number.isFinite(rateQ.data.value)
      ? rateQ.data.value
      : DEFAULT_NET_EXPO_PAGE_COMMISSION_RATE
  const [draft, setDraft] = useState(() => String(DEFAULT_NET_EXPO_PAGE_COMMISSION_RATE))
  const [totalMulti, setTotalMulti] = useState(DEFAULT_NET_EXPO_PAGE_COMMISSION_RATE)
  const cols = padRunnerCount(runners.length, book.type)
  const lineSession = isLineSessionMarket({
    marketId: book.marketId,
    marketName: book.marketName,
    lineLadder: book.lineLadder,
  })

  useEffect(() => {
    if (!rateQ.isSuccess || !Number.isFinite(rateQ.data.value)) return
    setTotalMulti(rateQ.data.value)
    setDraft(String(rateQ.data.value))
  }, [rateQ.isSuccess, rateQ.data?.value])

  const applyMulti = () => {
    const next = parseNetExpoPageCommissionRate(draft, serverRate)
    setDraft(String(next))
    if (!rateQ.live) {
      // Fixture / no CM session — apply locally (Lab default when CONFIG-V2 unavailable).
      setTotalMulti(next)
      return
    }
    if (setRate.isPending) return
    setRate.mutate(next, {
      onSuccess: (res) => {
        if (isNetExpoPageCommissionRateSaved(res)) {
          const applied = res.value > 0 ? res.value : next
          setTotalMulti(applied)
          setDraft(String(applied))
          toast.success(res.message || 'Commission rate changed successfully')
        } else {
          toast.error(res.message || 'Unable to update commission rate')
        }
      },
      onError: (err: Error) => {
        toast.error(err.message || 'Unable to update commission rate')
      },
    })
  }

  const runnerCells = (render: (runner: WorkstationRunner | undefined, index: number) => ReactNode) =>
    Array.from({ length: cols }, (_, i) => (
      <td key={runners[i]?.id ?? `pad-${i}`} className="w7-ws-tree-cell">
        {render(runners[i], i)}
      </td>
    ))

  /** Lab LS: Commission column after NE (0 on BF rows); runner pads blank. */
  const lsCommissionCell = (key: string) =>
    lineSession ? (
      <td key={key} className="w7-ws-tree-cell w7-ws-tree-comm">
        <Amt value={0} bookType={bookType} level="agent" lineSession />
      </td>
    ) : null

  const lsBlankRunners = (prefix: string) =>
    Array.from({ length: cols }, (_, i) => (
      <td key={`${prefix}-${runners[i]?.id ?? i}`} className="w7-ws-tree-cell">
        <span className="w7-ne-dash">&nbsp;</span>
      </td>
    ))

  return (
    <>
      {showBetfairTotalXRateRow(lineSession) ? (
        <tr className="w7-ws-bf-row" data-kind="total-x">
          <td className="w7-ws-tree-user">
            <span className="w7-ws-bf-label">
              Total (X){' '}
              <input
                className="w7-ws-bf-multi"
                type="text"
                inputMode="decimal"
                aria-label="Total multiplier"
                value={draft}
                disabled={setRate.isPending}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyMulti()
                }}
              />
              <button
                type="button"
                className="w7-ws-bf-update"
                disabled={setRate.isPending}
                onClick={applyMulti}
              >
                {setRate.isPending ? '…' : 'update'}
              </button>
            </span>
          </td>
          <td className="w7-ws-tree-expand" />
          <td className="w7-ws-tree-ne" />
          {lsCommissionCell('total-x-comm')}
          {runnerCells((runner, i) => {
            if (!runner) return <span className="w7-ne-dash">-</span>
            const local = amountForRunner(compare, runner.id, 'local', i)
            const bookAmt = treeHorseAmount(book.tree, runner.id, bookType)
            const total = local * totalMulti + bookAmt
            return (
              <Amt
                value={total}
                bookType={bookType}
                level="agent"
                lineSession={lineSession}
              />
            )
          })}
        </tr>
      ) : null}

      <tr className="w7-ws-bf-row" data-kind="betfair">
        <td className="w7-ws-tree-user">
          <span className="w7-ws-bf-label">Betfair Book - betfair</span>
        </td>
        <td className="w7-ws-tree-expand" />
        <td className="w7-ws-tree-ne">
          <Amt
            value={lineSessionBetfairBookDisplayAmount(
              lineSession,
              Number(compare.netExposureBetfair) || 0,
            )}
            bookType={bookType}
            level="agent"
            lineSession={lineSession}
          />
        </td>
        {lsCommissionCell('bf-comm')}
        {lineSession
          ? lsBlankRunners('bf')
          : runnerCells((runner, i) => {
              if (!runner) return <span className="w7-ne-dash">-</span>
              const amount = amountForRunner(compare, runner.id, 'betfair', i)
              return (
                <Amt
                  value={amount}
                  bookType={bookType}
                  level="agent"
                  lineSession={lineSession}
                />
              )
            })}
      </tr>

      <tr className="w7-ws-bf-row" data-kind="local">
        <td className="w7-ws-tree-user">
          <span className="w7-ws-bf-label">Betfair Book - local</span>
        </td>
        <td className="w7-ws-tree-expand" />
        <td className="w7-ws-tree-ne">
          <Amt
            value={lineSessionBetfairBookDisplayAmount(
              lineSession,
              compare.netExposureLocal,
            )}
            bookType={bookType}
            level="agent"
            lineSession={lineSession}
          />
        </td>
        {lsCommissionCell('local-comm')}
        {lineSession
          ? lsBlankRunners('local')
          : runnerCells((runner, i) => {
              if (!runner) return <span className="w7-ne-dash">-</span>
              const amount = amountForRunner(compare, runner.id, 'local', i)
              return (
                <Amt
                  value={amount}
                  bookType={bookType}
                  level="agent"
                  lineSession={lineSession}
                />
              )
            })}
      </tr>
    </>
  )
}

export function BookTree({
  book,
  bookType,
  betfairCompare = null,
  live = false,
}: {
  book: WorkstationBook
  bookType: BookType
  betfairCompare?: BetfairCompareView | null
  /** Live CM session — family icon fetches downline via CM-API. */
  live?: boolean
}) {
  // Lab starts with currentlyShowing=false (family icon visible, children hidden).
  const [open, setOpen] = useState<Set<string>>(() => new Set())
  const [ladderOpen, setLadderOpen] = useState<Set<string>>(() => new Set())
  const runners = book.runners
  const cols = padRunnerCount(runners.length, book.type)
  const lineSession = isLineSessionMarket({
    marketId: book.marketId,
    marketName: book.marketName,
    lineLadder: book.lineLadder,
  })

  const onToggle = (id: string) => {
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  /** Toggle one row's PT ladder without closing siblings (CM + TM both open). */
  const onToggleLadder = (id: string) => {
    setLadderOpen((prev) => nextPtLadderOpen(prev, id))
  }

  return (
    <div className="w7-ws-tree">
      <div className="w7-ws-tree-market">
        {labeledMarketName(
          {
            marketId: book.marketId,
            marketName: book.marketName,
            lineLadder: book.lineLadder,
          },
          book.type,
        )}
      </div>
      <div className="w7-ws-tree-scroll">
        <table className="w7-ws-tree-table">
          <thead>
            <tr>
              <th className="w7-ws-tree-user" />
              <th className="w7-ws-tree-expand" />
              <th className="w7-ws-tree-ne">Net Exposure</th>
              {lineSession ? (
                <th className="w7-ws-tree-cell w7-ws-tree-comm">Commission</th>
              ) : null}
              {Array.from({ length: cols }, (_, i) => {
                const runner = runners[i]
                return (
                  <th key={runner?.id ?? `pad-${i}`} className="w7-ws-tree-cell">
                    {lineSession ? '\u00a0' : (runner?.name ?? '\u00a0')}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {showBetfairBookPanel(book) ? (
              <BetfairBookRows
                book={book}
                bookType={bookType}
                compare={betfairCompare ?? emptyBetfairCompareMatch(runners)}
                runners={runners}
              />
            ) : null}
            <TreeRow
              node={book.tree}
              runners={runners}
              type={book.type}
              bookType={bookType}
              depth={0}
              open={open}
              onToggle={onToggle}
              marketId={book.marketId}
              live={live}
              ladderOpen={ladderOpen}
              onToggleLadder={onToggleLadder}
              lineSession={lineSession}
            />
          </tbody>
        </table>
      </div>
    </div>
  )
}
